'use client';

import { useState, useEffect } from 'react';
import SearchBar from '@/components/common/SearchBar';
import FilterPanel from '@/components/common/FilterPanel';
import SortControls from './SortControls';
import MechanicCard from './MechanicCard';
import FeaturedMechanic from './FeaturedMechanic';
import QuickActions from './QuickActions';
import Icon from '@/components/ui/AppIcon';

interface Mechanic {
  id: string;
  name: string;
  image: string;
  imageAlt: string;
  rating: number;
  reviewCount: number;
  distance: number;
  specializations: string[];
  services: string[];
  isVerified: boolean;
  phoneNumber: string;
  address: string;
  reviewHighlight: string;
  availability: string;
}

const SearchResultsInteractive = () => {
  const [isHydrated, setIsHydrated] = useState(false);
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [currentSort, setCurrentSort] = useState('rating'); // Default to rating for curated list
  const [mechanics, setMechanics] = useState<Mechanic[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [currentSearchParams, setCurrentSearchParams] = useState({ brand: '', location: '', service: '' });

  useEffect(() => {
    setIsHydrated(true);
  }, []);

  useEffect(() => {
    const fetchMechanics = async () => {
      if (!isHydrated) return;

      try {
        setIsLoading(true);
        // Get brand from URL if present
        const searchParams = new URLSearchParams(window.location.search);
        const brand = searchParams.get('brand') || '';
        const location = searchParams.get('location') || '';
        const service = searchParams.get('service') || '';

        setCurrentSearchParams({ brand, location, service });

        const apiUrl = new URL('/api/mechanics', window.location.origin);
        if (brand) apiUrl.searchParams.append('brand', brand);
        if (location) apiUrl.searchParams.append('location', location);
        if (service) apiUrl.searchParams.append('service', service);

        const response = await fetch(apiUrl.toString());
        const result = await response.json();

        if (result.success) {
          const mappedMechanics: Mechanic[] = result.data.map((m: any) => ({
            id: m.id,
            name: m.business_name,
            image: m.shop_type === 'dealership'
              ? "https://images.unsplash.com/photo-1726273547157-f6d8b6ddb6f0"
              : "https://images.unsplash.com/photo-1717251219558-40dae5f9b195",
            imageAlt: `${m.business_name} shop exterior`,
            rating: Number(m.rating),
            reviewCount: m.review_count,
            distance: 0.0, // Calculated distance not yet implemented
            specializations: m.specializations || [],
            services: m.specializations || [], // Reuse specializations for now
            isVerified: m.verified,
            phoneNumber: m.phone,
            address: `${m.city}, ${m.zip_code}`,
            reviewHighlight: m.description || 'Excellent independent service for BMW and Lexus vehicles.',
            availability: 'Open Now • Closes at 6:00 PM'
          }));
          setMechanics(mappedMechanics);
        } else {
          setError(result.error || 'Failed to fetch mechanics');
        }
      } catch (err) {
        setError('Failed to connect to the database');
      } finally {
        setIsLoading(false);
      }
    };

    fetchMechanics();
  }, [isHydrated]);



  const handleSortChange = (sort: string) => {
    setCurrentSort(sort);
    // Sort logic would be implemented here
  };

  const handleFilterToggle = () => {
    setIsFilterOpen(!isFilterOpen);
  };

  const handleFilterClose = () => {
    setIsFilterOpen(false);
  };

  if (!isHydrated) {
    return (
      <div className="min-h-screen bg-background pt-16">
        <div className="mx-auto max-w-[1400px] px-4 sm:px-6 lg:px-8 py-8">
          <div className="animate-pulse space-y-6">
            <div className="h-32 bg-muted rounded-xl" />
            <div className="h-16 bg-muted rounded-lg" />
            <div className="grid grid-cols-1 gap-6">
              {[1, 2, 3].map((i) =>
                <div key={i} className="h-64 bg-muted rounded-xl" />
              )}
            </div>
          </div>
        </div>
      </div>);

  }

  return (
    <div className="min-h-screen bg-background pt-16">
      <div className="mx-auto max-w-[1400px] px-4 sm:px-6 lg:px-8 py-8">
        <div className="space-y-6">
          {/* Search Bar */}
          <SearchBar variant="condensed" />

          {/* Primary External Search Action */}
          <div className="bg-gradient-to-r from-blue-600/10 to-red-600/10 rounded-2xl p-6 sm:p-8 border border-border flex flex-col items-center text-center shadow-sm">
            <h2 className="text-2xl sm:text-3xl font-heading font-bold text-text-primary mb-3">
              Find the Best Rated Mechanics Near You
            </h2>
            <p className="text-text-secondary max-w-2xl mx-auto mb-8 text-sm sm:text-base">
              Get real-time, comprehensive results from the most trusted local review platforms. Discover independent specialists and certified repair shops instantly.
            </p>
            
            <div className="flex flex-col sm:flex-row w-full sm:w-auto gap-4 justify-center">
              <a 
                href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(`${currentSearchParams.brand} ${currentSearchParams.service} auto repair mechanic ${currentSearchParams.location}`.trim().replace(/\s+/g, ' '))}`}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center justify-center space-x-3 rounded-xl bg-blue-600 px-8 py-4 font-body text-base font-bold text-white transition-smooth hover:bg-blue-700 hover:shadow-lg active:scale-[0.98]"
              >
                <Icon name="MapPinIcon" size={22} />
                <span>Search on Google Maps</span>
              </a>
              
              <a 
                href={`https://www.yelp.com/search?find_desc=${encodeURIComponent(`${currentSearchParams.brand} ${currentSearchParams.service} auto repair mechanic`.trim().replace(/\s+/g, ' '))}&find_loc=${encodeURIComponent(currentSearchParams.location || 'current location')}`}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center justify-center space-x-3 rounded-xl bg-red-600 px-8 py-4 font-body text-base font-bold text-white transition-smooth hover:bg-red-700 hover:shadow-lg active:scale-[0.98]"
              >
                <Icon name="StarIcon" size={22} />
                <span>Search on Yelp</span>
              </a>
            </div>
          </div>

          {/* Quick Actions */}
          <QuickActions />

          {/* Main Content */}
          <div className="flex flex-col lg:flex-row gap-6">
            {/* Filter Panel - Desktop */}
            <div className="hidden lg:block lg:w-80 flex-shrink-0">
              <div className="sticky top-24">
                <FilterPanel isOpen={true} />
              </div>
            </div>

            {/* Results Section */}
            <div className="flex-1 space-y-6">
              {/* Mobile Filter Button */}
              <div className="lg:hidden">
                <button
                  onClick={handleFilterToggle}
                  className="flex items-center justify-center space-x-2 w-full rounded-lg border border-border bg-card px-4 py-3 font-body text-sm font-medium text-text-secondary transition-smooth hover:border-primary hover:text-primary active:scale-[0.97]">

                  <Icon name="AdjustmentsHorizontalIcon" size={20} />
                  <span>Filters & Sort</span>
                </button>
              </div>

              {/* Sort Controls (Hidden if no native results or if native results are loading) */}
              {!isLoading && !error && mechanics.length > 0 && (
                <SortControls
                  currentSort={currentSort}
                  onSortChange={handleSortChange}
                  resultCount={mechanics.length} />
              )}

              {/* Verified Network Divider */}
              <div className="pt-8 pb-4">
                <div className="flex items-center">
                  <div className="flex-grow border-t border-border"></div>
                  <span className="flex-shrink-0 mx-4 text-sm font-medium text-text-secondary">
                    AutoFixIQ Verified Network (Secondary)
                  </span>
                  <div className="flex-grow border-t border-border"></div>
                </div>
              </div>
              <div className="space-y-6">
                {isLoading ? (
                  <div className="flex flex-col items-center justify-center py-12 space-y-4">
                    <Icon name="ArrowPathIcon" size={32} className="animate-spin text-primary" />
                    <p className="text-text-secondary">Finding mechanics...</p>
                  </div>
                ) : error ? (
                  <div className="rounded-xl border border-error/20 bg-error/5 p-8 text-center">
                    <Icon name="ExclamationCircleIcon" size={32} className="mx-auto text-error mb-4" />
                    <p className="text-error font-medium">{error}</p>
                    <button onClick={() => window.location.reload()} className="mt-4 text-sm text-primary underline">Try again</button>
                  </div>
                ) : mechanics.length === 0 ? (
                  <div className="rounded-xl border border-dashed border-border p-8 py-12 flex flex-col items-center text-center bg-card/50">
                    <Icon name="InformationCircleIcon" size={32} className="text-muted-foreground mb-4" />
                    <h3 className="text-lg font-medium text-text-primary mb-2">No Verified Network Partners Found</h3>
                    <p className="text-sm text-text-secondary max-w-md mx-auto">
                      We haven't onboarded mechanics matching this exact criteria yet. Please use the Google Maps or Yelp options above to find highly rated local shops.
                    </p>
                  </div>
                ) : (
                  mechanics.map((mechanic) => (
                    <MechanicCard key={mechanic.id} mechanic={mechanic} />
                  ))
                )}
              </div>

              {/* Load More */}
              {!isLoading && !error && mechanics.length > 0 && (
                <div className="flex justify-center pt-6">
                  <button className="flex items-center justify-center space-x-2 rounded-lg border border-border bg-card px-8 py-3 font-body text-sm font-medium text-text-secondary transition-smooth hover:border-primary hover:bg-muted hover:text-primary active:scale-[0.97]">
                    <Icon name="ArrowPathIcon" size={18} />
                    <span>Load More Results</span>
                  </button>
                </div>
              )}
            </div>
          </div>

          {/* Mobile Filter Panel */}
          {isFilterOpen &&
            <FilterPanel
              isOpen={isFilterOpen}
              onClose={handleFilterClose} />

          }
        </div>
      </div>
    </div>);

};

export default SearchResultsInteractive;